<script setup lang="ts">
import type { UppyOptions } from "@uppy/core"
import Dashboard from "@uppy/vue/dashboard"
import { useLocalStorage, useUrlSearchParams } from "@vueuse/core"
import { computed, onMounted, reactive, ref, watchEffect } from "vue"
import { notifyUploadComplete } from "@/api"
import { Alert, Checkbox } from "@/components/ui"
import {
  s3Plugin,
  tusPlugin,
  useTranslation,
  useUppy,
  webcamPlugin,
} from "@/composables"
import { ForbiddenException, getErrorCode } from "@/http"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { useToast } from "../ui/ToastReka/useToast"

import "@uppy/core/css/style.min.css"
import "@uppy/dashboard/css/style.min.css"
import "@uppy/webcam/css/style.min.css"

interface UploaderProps {
  allowCamera?: boolean
  autoProceed?: boolean
  canUploadVideos?: boolean
  additionalFileMeta?: object
  disabled?: boolean
  directUploads?: boolean
}

interface UploadErrorWithContext extends Error {
  context?: {
    errorCode?: string
  }
}

const props = withDefaults(defineProps<UploaderProps>(), {
  allowCamera: false,
  autoProceed: false,
  canUploadVideos: false,
  additionalFileMeta: () => ({}),
  disabled: false,
  directUploads: false,
})

const emit = defineEmits<{
  (e: "success", results: unknown[]): void
  (e: "error", results: unknown[]): void
}>()

const helpLinkByBrand: Record<string, string> = {
  guestpix: "https://help.guestpix.com/category/167-uploading-downloading",
  honorpix: "https://help.honorpix.com/category/204-uploads-and-downloads",
}

function getHelpLink(): string {
  const brand = import.meta.env.VITE_BRAND || "guestpix"
  return helpLinkByBrand[brand] ?? helpLinkByBrand.guestpix
}

const helpLink = getHelpLink()

const { t } = useTranslation()
const toast = useToast()
const eventVaultStore = useEventVaultStore()

const urlParams = useUrlSearchParams<{ album?: unknown }>()
const baseAllowedFormats = ref(
  new Set([
    "image/jpeg",
    "image/png",
    "image/bmp",
    "image/gif",
    "image/webp",
    ".heic",
    ".heif",
  ]),
)
const uploadedFiles = useLocalStorage<string[]>(
  `uploaded-${eventVaultStore.eventVaultId}`,
  [],
)
const allowDuplicates = ref(true)
const isMobile = ref(false)
const isIos = ref(false)
const suppressFailedToast = ref(false)

const tus = tusPlugin()
const webcam = webcamPlugin()

const uppyOptions: UppyOptions<
  Record<string, unknown>,
  Record<string, unknown>
> = reactive({
  autoProceed: props.autoProceed,
  restrictions: {
    maxFileSize: null,
    minFileSize: null,
    maxTotalFileSize: null,
    minNumberOfFiles: null,
    requiredMetaFields: [],
    allowedFileTypes: [...baseAllowedFormats.value.values()],
    maxNumberOfFiles: import.meta.env.VITE_TUS_MAX_FILE_COUNT
      ? Number.parseInt(import.meta.env.VITE_TUS_MAX_FILE_COUNT)
      : 100,
  },
  onBeforeFileAdded: (file) => {
    if (allowDuplicates.value) {
      return true
    }

    if (uploadedFiles.value && file?.name && uploadedFiles.value.includes(file.name)) {
      // eslint-disable-next-line ts/no-use-before-define
      uppy?.info(t("uploader.skip_duplicate", { filename: file.name }), "error")

      return false
    }
  },
})

const {
  uppy,
  addPlugin,
  removePlugin,
  addFileRestriction,
  removeFileRestriction,
  uploadError,
} = useUppy(uppyOptions)

const s3 = computed(() =>
  eventVaultStore.eventVaultId ? s3Plugin(uppy, t) : null,
)

function configureUploadPlugins() {
  if (s3.value?.name && uppy.getPlugin(s3.value.name)) {
    removePlugin(s3.value.name)
  }

  if (uppy.getPlugin(tus.name)) {
    removePlugin(tus.name)
  }

  if (props.directUploads && s3.value) {
    addPlugin(s3.value.name, s3.value.plugin, s3.value.options)
  }
  else {
    addPlugin(tus.name, tus.plugin, tus.options)
  }
}

onMounted(() => {
  isIos.value = /iPhone|iPod|iPad/i.test(navigator.userAgent)
  isMobile.value = /iPhone|iPod|Android/i.test(navigator.userAgent)

  if (urlParams.album && typeof urlParams.album === "string") {
    eventVaultStore.setAlbumId(Number(urlParams.album))
  }

  uppy.on("file-added", (file) => {
    uppy.setFileMeta(file.id, {
      eventVaultId: eventVaultStore.eventVaultId,
      fileLength: file.size,
      fileExtension: file.extension,
      eventAlbumId: eventVaultStore.albumId,
      contentType: file.type,
      ...props.additionalFileMeta,
    })
  })

  uppy.on("upload-success", async (file) => {
    if (!file) {
      return
    }

    if (!eventVaultStore.eventVaultId) {
      throw new Error("Missing eventVaultId")
    }

    if (file?.name && !uploadedFiles.value.includes(file.name)) {
      uploadedFiles.value.push(file.name)
    }

    if (props.directUploads) {
      const { name, size, type, meta } = file

      const key = typeof meta.key === "string" ? meta.key : String(meta.key)
      const fileName
        = typeof meta.uFileName === "string"
          ? meta.uFileName
          : String(meta.uFileName)

      if (key) {
        try {
          await notifyUploadComplete(
            {
              ...meta,
              key,
              fileName,
              originalFileName: name,
              fileSize: size,
              contentType: type,
            },
            eventVaultStore.eventVaultId,
            eventVaultStore.albumId,
          )
        }
        catch (error) {
          if (error instanceof ForbiddenException) {
            const errorCode = getErrorCode(error.response?.data)
            const message = errorCode
              ? t(`error_codes.${errorCode}` as const)
              : t("something_went_wrong")
            uploadError.value = { errorCode: errorCode ?? "", message }

            uppy.info(message, "error")

            return
          }

          throw error
        }
      }
    }
  })

  uppy.on("upload-error", (_, error) => {
    const errorWithContext = error as UploadErrorWithContext

    if (typeof errorWithContext.context?.errorCode === "string") {
      uploadError.value = {
        errorCode: errorWithContext.context.errorCode,
        message: error.message,
      }
      suppressFailedToast.value = true
    }

    const errorMessage = error?.message || t("uploader.error.upload_failed")
    uppy.info(errorMessage, "error")
  })

  uppy.on("complete", ({ successful, failed }) => {
    if (successful && successful.length) {
      emit("success", successful)
      toast.success({
        title: t("uploader.success.upload_success"),
        icon: "FolderCheck",
      })
    }

    if (failed && failed.length) {
      emit("error", failed)
      if (!suppressFailedToast.value) {
        toast.error({
          icon: "FolderCross",
          title: t("uploader.error.upload_failed"),
          description: t("uploader.error.failed_message"),
          action: {
            label: t("uploader.error.review_tips"),
            href: helpLink,
            target: "_blank",
            rel: "noopener noreferrer",
          },
        })
      }
    }

    suppressFailedToast.value = false

    clearAfterComplete()
  })
})

function clearAfterComplete() {
  uppy.getFiles().forEach(f => uppy.removeFile(f.id))
  uppy.setState({ totalProgress: 0 })
}

// The watch effect is needed so that if the drawer component that the uploader is used
// in has a keep alive prop, the uploader props that change, will still be updated on the instance of uppy.
watchEffect(() => {
  uppyOptions.autoProceed = props.autoProceed

  if (isMobile.value)
    addFileRestriction("text/plain")
  else removeFileRestriction("text/plain")

  if (props.canUploadVideos)
    addFileRestriction("video/*")
  else removeFileRestriction("video/*")

  if (props.allowCamera) {
    addPlugin(webcam.name, webcam.plugin, webcam.options)
  }
  else {
    removePlugin(webcam.name)
  }
})

watchEffect(() => {
  if (eventVaultStore.eventVaultId) {
    configureUploadPlugins()
  }
})

watchEffect(() => {
  if (!uploadError.value) {
    return
  }

  const { message } = uploadError.value

  toast.error({
    icon: "Caution",
    title: t("uploader.error.upload_failed"),
    description: message,
  })

  uploadError.value = null
})

const dashboardOptions = {
  proudlyDisplayPoweredByUppy: false,
  hideProgressAfterFinish: true,
  height: 300,
}

const dashboardPlugins = computed(() => {
  const plugins = []

  if (props.allowCamera)
    plugins.push("Webcam")

  return plugins
})
</script>

<template>
  <div v-if="!disabled && uppy">
    <Alert v-if="isIos" variant="info" closable class="mb-4" @dismiss="isIos = false">
      <i18n-t keypath="uploader.ios_alert" scope="global">
        <template #hi>
          <span class="font-bold">{{ t("uploader.hi_there") }}</span>
        </template>
      </i18n-t>
      <br>
      <a
        :href="helpLink"
        target="_blank"
        rel="noopener noreferrer"
        class="underline mt-5"
      >
        {{ t("uploader.more_info_here") }}
      </a>
    </Alert>
    <div
      class="gpx-uploader-duplicates-checkbox flex justify-between items-center"
    >
      <Checkbox
        v-model="allowDuplicates"
        name="allowDuplicates"
        :label="t('uploader.bypass_duplicate_label')"
      />
      <a
        class="gpx-button gpx-button_flat"
        :href="helpLink"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ t("help") }}
      </a>
    </div>

    <Dashboard
      :uppy="uppy"
      :props="dashboardOptions"
      :plugins="dashboardPlugins"
    />
  </div>
  <Alert v-else :closable="false" variant="danger" prefix-icon="Caution">
    {{ t("uploader.disabled") }}
  </Alert>
</template>

<style scoped>
.gpx-uploader-duplicates-checkbox {
  margin-bottom: 1rem;
}

.gpx-uploader-toast-content {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.gpx-uploader-toast-content a {
  text-decoration: underline;
  cursor: pointer;
  color: white;
}

.gpx-uploader-toast-content-title {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  white-space: nowrap;
}

.gpx-uploader-toast-content-title h5 {
  font-size: 1rem;
  margin: 0 1rem 0 0;
}

.gpx-uploader-toast-content p {
  font-family: montserrat, sans-serif;
  font-weight: var(--gpx-font-normal);
  font-size: 0.875rem;
  margin: 0;
  max-width: 250px;
}

@media (width >=768px) {
  .gpx-uploader-toast-content-title h5 {
    font-size: 1.15rem;
  }
}
</style>
